import React, { useCallback, useEffect, useRef, useState } from 'react';

import { toastSuccess, toastError } from '@components/Toast';

import { ReactComponent as UploadIcon } from '@/assets/icons/upload-gray.svg';
import { ReactComponent as UploadedIcon } from '@/assets/icons/success-page.svg';
import { ReactComponent as UploadingIcon } from '@/assets/icons/Load.svg';
import { ReactComponent as FailedIcon } from '@/assets/icons/alert-triangle.svg';
import CommonImage from '@/components/CommonImage';
import LoaderComponent from '@/components/LoaderComponent';

import {
	useUploadMultipleTechpackMutation,
	useUploadFileToS3Mutation,
	useUploadDocumentMutation,
	useUploadFormInspirationMutation,
	useGetExtractionPendingListQuery,
	useProductAnalyzeDocumentMutation,
	useRemoveFileFromCollectionMutation
} from '../api/MultipleTechpackJourneyApi';
import { isAllowedTechpackFile, docMimeTypeForFile } from '../utils';

const MultipleUploadTechpack = ({ collectionId, onUpdateBasicInfo = () => {}, MergedFilesCount }) => {
	const [uploadedFiles, setUploadedFiles] = useState([]);
	const [isDragging, setIsDragging] = useState(false);
	const [documentIds, setDocumentIds] = useState([]);
	const [extractionPendingList, setExtractionPendingList] = useState([]);
	const uploadInputRef = useRef(null);
	const shouldRefetchAfterDocumentFlow = useRef(false);

	const [uploadMultipleTechpack, { isLoading: isUploadingMultipleTechpackLoading }] =
		useUploadMultipleTechpackMutation();
	const [uploadFileToS3] = useUploadFileToS3Mutation();
	const [uploadDocument, { isLoading: isUploadingDocumentLoading }] = useUploadDocumentMutation();
	const [uploadFormInspiration, { isLoading: isUploadingFormInspirationLoading }] =
		useUploadFormInspirationMutation();
	const [removeFileFromCollection, { isLoading: isRemovingFileFromCollectionLoading }] =
		useRemoveFileFromCollectionMutation();
	const [productAnalyzeDocument, { isLoading: isAnalyzingDocumentLoading }] =
		useProductAnalyzeDocumentMutation();
	const { data: extractionPendingListData, refetch: refetchExtractionPendingList } =
		useGetExtractionPendingListQuery({ collectionId }, { skip: !collectionId });

	//refresh server list whenever page is entered
	useEffect(() => {
		if (!collectionId) return;
		refetchExtractionPendingList();
	}, [collectionId, refetchExtractionPendingList]);

	///analyze document response to add form pending-info-extraction
	const onAnalyzeDocument = async (productIds) => {
		try {
			await productAnalyzeDocument({
				productIds
			}).unwrap();
		} catch (err) {
			toastError(err?.data?.message || err?.error || err?.message || 'Upload failed.');
		}
	};

	//extraction pending list
	useEffect(() => {
		const responseExtractionPendingList =
			extractionPendingListData?.payload ??
			extractionPendingListData?.data ??
			extractionPendingListData ??
			[];

		const list = Array.isArray(responseExtractionPendingList)
			? responseExtractionPendingList
			: responseExtractionPendingList && typeof responseExtractionPendingList === 'object'
				? Object.values(responseExtractionPendingList)
				: [];
		const fileNames = list
			.map((item) => ({
				productId: item?.productId,
				name: item?.documentResponse?.name,
				docUrl: item?.documentResponse?.docUrl
			}))
			.filter((item) => item.productId && item.name);
		const dedupedPending = Array.from(
			new Map(
				fileNames.map((item) => [
					item.productId || item.docUrl || (item.name || '').trim().toLowerCase(),
					item
				])
			).values()
		);
		setExtractionPendingList(dedupedPending);
	}, [extractionPendingListData]);

	const extractionPendingFileEntries = extractionPendingList.map((item) => {
		return {
			id: item.productId,
			productId: item.productId,
			name: item?.name,
			size: '',
			status: 'success'
		};
	});

	// Always combine local upload rows with server pending list.
	const filesForMerge = [...uploadedFiles, ...extractionPendingFileEntries];

	// file merge list
	const mergedFilesMap = new Map();
	filesForMerge.forEach((file) => {
		const productId = file?.productId ?? file?.backendProductId;
		const key =
			productId || file?.docUrl || (file?.name || '').trim().toLowerCase() || String(file?.id || '');
		const existing = mergedFilesMap.get(key);
		if (!existing) {
			mergedFilesMap.set(key, file);
			return;
		}
		const existingProductId = existing?.productId ?? existing?.backendProductId;
		if (!existingProductId && productId) {
			mergedFilesMap.set(key, file);
		}
	});
	const mergedFiles = Array.from(mergedFilesMap.values()).sort((a, b) =>
		(a?.name || '').localeCompare(b?.name || '', undefined, { sensitivity: 'base' })
	);

	useEffect(() => {
		MergedFilesCount?.(mergedFiles.length);
	}, [mergedFiles.length, MergedFilesCount]);

	//add form inspiration
	const addFormInspiration = useCallback(
		async (response) => {
			try {
				const techpackIdProductDesignIdMapList =
					response !== null
						? response.map((item) => {
								return {
									techpackId: item?.techPackId,
									productDesignId: item?.designlDocumentId
								};
							})
						: [];
				await uploadFormInspiration({
					collectionId,
					techpackIdProductDesignIdMapList
				}).unwrap();
			} catch (err) {
				toastError(err?.message || 'Upload failed.');
				throw err;
			}
		},
		[collectionId, uploadFormInspiration]
	);

	useEffect(() => {
		if (documentIds.length === 0) return;

		const getDocumentResponse = async () => {
			try {
				const response = await uploadDocument(documentIds).unwrap();
				await addFormInspiration(response);
				if (shouldRefetchAfterDocumentFlow.current) {
					await refetchExtractionPendingList();
					// After successful server sync, keep only in-flight/failed local rows.
					setUploadedFiles((prev) => prev.filter((file) => file.status !== 'success'));
				}
			} catch (err) {
				toastError(err?.message || 'Upload failed.');
			} finally {
				shouldRefetchAfterDocumentFlow.current = false;
				setDocumentIds([]);
			}
		};
		getDocumentResponse();
	}, [addFormInspiration, documentIds, refetchExtractionPendingList, uploadDocument]);

	/// file upload process to initiate
	const onAddFiles = async (files) => {
		const selectedFiles = Array.from(files || []);
		if (selectedFiles.length === 0) return;
		if (selectedFiles.length > 100) {
			toastError('You can upload up to 100 files.');
			return;
		}

		const allowedFiles = selectedFiles.filter(isAllowedTechpackFile);
		const capped = allowedFiles.slice(0, 100);

		const collId = collectionId;
		if (!collId) {
			toastError('Collection is required to upload techpacks.');
			return;
		}

		const newEntries = capped.map((file, idx) => ({
			id: `${file.name}-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 9)}`,
			name: file.name,
			size: `${(file.size / (1024 * 1024)).toFixed(2)} MB`,
			status: 'uploading',
			backendProductId: null
		}));

		setUploadedFiles((prev) => [...prev, ...newEntries]);
		try {
			const documentDTOList = capped.map((file) => ({
				name: file.name,
				docMimeType: docMimeTypeForFile(file),
				documentType: file.type === 'application/pdf' ? 'TECH_PACK_DESIGN' : 'PRODUCT_DESIGN'
			}));

			const response = await uploadMultipleTechpack({
				collectionId: collId,
				documentDTOList
			}).unwrap();

			const uploadResults = await Promise.all(
				capped.map(async (file, index) => {
					const entryId = newEntries[index].id;
					const doc = response[index];
					const url = doc?.urlList?.[0];
					if (!url) {
						setUploadedFiles((prev) =>
							prev.map((f) => (f.id === entryId ? { ...f, status: 'failed' } : f))
						);
						return false;
					}

					try {
						await uploadFileToS3({ url, file }).unwrap();
						setUploadedFiles((prev) =>
							prev.map((f) =>
								f.id === entryId
									? {
											...f,
											status: 'success',
											backendProductId: doc?.productId ?? doc?.id ?? f.backendProductId
										}
									: f
							)
						);
						return true;
					} catch {
						setUploadedFiles((prev) =>
							prev.map((f) => (f.id === entryId ? { ...f, status: 'failed' } : f))
						);
						return false;
					}
				})
			);
			const allFilesUploadedSuccessfully = uploadResults.every(Boolean);
			shouldRefetchAfterDocumentFlow.current = allFilesUploadedSuccessfully;

			const extractedDocumentIds = response
				.map((item) => item?.documentId)
				.filter((id) => id !== null && id !== undefined);
			if (extractedDocumentIds.length > 0) {
				setDocumentIds(extractedDocumentIds);
			}
		} catch (err) {
			toastError(err?.message || 'Upload failed.');
			const batchIds = new Set(newEntries.map((e) => e.id));
			setUploadedFiles((prev) =>
				prev.map((f) => (batchIds.has(f.id) ? { ...f, status: 'failed' } : f))
			);
		}
	};

	const onRemoveFile = async (file, collectionId) => {
		const fileId = file?.id;
		const removableProductId = file?.productId ?? file?.backendProductId;
		setExtractionPendingList((prev) => prev.filter((item) => item.productId !== removableProductId));
		setUploadedFiles((prev) => prev.filter((item) => item.id !== fileId));
		if (!removableProductId) {
			toastSuccess('File removed from list');
			return;
		}
		try {
			await removeFileFromCollection({
				id: collectionId,
				productIds: [removableProductId]
			}).unwrap();
			await refetchExtractionPendingList();
			toastSuccess('File removed successfully');
		} catch (err) {
			toastError(err?.data?.message || 'Failed to remove file');
		}
	};

	const onOpenFileDialog = () => {
		if (uploadInputRef.current) {
			uploadInputRef.current.click();
		}
	};

	const onFileChange = (event) => {
		onAddFiles(event.target.files);
		event.target.value = null;
	};

	const onDragOver = (event) => {
		event.preventDefault();
		setIsDragging(true);
	};

	const onDragLeave = (event) => {
		event.preventDefault();
		setIsDragging(false);
	};

	const onDrop = (event) => {
		event.preventDefault();
		setIsDragging(false);
		onAddFiles(event.dataTransfer.files);
	};

	const onUpdateBasicInfoClick = async () => {
		const productIds = extractionPendingList.map((item) => item.productId).filter(Boolean);
		if (productIds.length > 0) {
			await onAnalyzeDocument(productIds);
		}
		onUpdateBasicInfo({ productIds, extractionPendingList });
	};

	const isUploadFlowRunning =
		isUploadingMultipleTechpackLoading ||
		isUploadingDocumentLoading ||
		isUploadingFormInspirationLoading ||
		isRemovingFileFromCollectionLoading ||
		isAnalyzingDocumentLoading ||
		uploadedFiles.some((file) => file.status === 'uploading');

	return (
		<LoaderComponent
			loading={
				isAnalyzingDocumentLoading ||
				isUploadingDocumentLoading ||
				isUploadingFormInspirationLoading ||
				isRemovingFileFromCollectionLoading ||
				isUploadingMultipleTechpackLoading
			}
		>
			<div className="multiple-upload-techpack-page">
				<h2 className="upload-page-title mb-12 weight-700">Upload Techpacks</h2>
				<p className="upload-page-subtitle regular-14 gray_dark_01">
					Upload up to 100 PDF, JPEG, or PNG techpacks. The system will automatically extract
					category, embellishment type, and body fabric information.
				</p>

				<div
					className={`techpack-dropzone ${isDragging ? 'dragging' : ''}`}
					onClick={onOpenFileDialog}
					onDragOver={onDragOver}
					onDragLeave={onDragLeave}
					onDrop={onDrop}
				>
					<input
						ref={uploadInputRef}
						type="file"
						accept="application/pdf,image/jpeg,image/png"
						multiple
						onChange={onFileChange}
						className="d-none"
					/>
					<div className="upload-arrow mb-2">
						<UploadIcon width={34} height={34} />
					</div>
					<p className="dropzone-title medium-14 gray_dark_02">Click to upload or drag and drop</p>
					<p className="dropzone-hint medium-14 gray_dark_02">PDF files up to 100 PDF</p>
				</div>
				{mergedFiles.length > 0 && (
					<div className="upload-file-card">
						<div className="upload-card-header">
							<p className="upload-count regular-14 gray_dark_01">
								Uploaded files: {mergedFiles.length}
							</p>
						</div>

						<div className="techpack-file-list scroll-y-label">
							{mergedFiles.map((file) => (
								<div key={file.id} className="techpack-file-row">
									<div className="file-left-content">
										<span
											className={`file-status-icon ${
												file.status === 'uploading'
													? 'uploading'
													: file.status === 'success'
														? 'uploaded'
														: 'failed'
											}`}
										>
											{file.status === 'uploading' && (
												<UploadingIcon
													className="uploading-icon"
													width={16}
													height={16}
												/>
											)}
											{file.status === 'success' && (
												<UploadedIcon width={16} height={16} />
											)}
											{file.status === 'failed' && (
												<FailedIcon width={16} height={16} />
											)}
										</span>

										<span
											className="close-icon cursor-pointer"
											onClick={() => onRemoveFile(file, collectionId)}
										>
											<CommonImage src="/icons/delete_red.svg" width={16} height={16} />
										</span>

										<span className="file-name regular-14 gray_dark_01">{file.name}</span>
									</div>
									<div className="file-right-content">
										<span className="file-size regular-14 gray_dark_01">{file.size}</span>
										{file.status === 'uploading' && (
											<span className="file-status regular-14 blue">Uploading..</span>
										)}
										{file.status === 'success' && (
											<span className="file-status regular-14 green">Uploaded</span>
										)}
										{file.status === 'failed' && (
											<span className="file-status regular-14 red">
												Uploaded failed
											</span>
										)}
									</div>
								</div>
							))}
						</div>

						<button
							type="button"
							className={`${mergedFiles.length > 0 ? 'cursor-pointer' : 'cursor-none'} update-basic-info-btn regular-14 text-white ${
								isUploadFlowRunning ? 'cursor-none' : ''
							}`}
							disabled={isUploadFlowRunning}
							onClick={onUpdateBasicInfoClick}
						>
							Update basic info with AI
						</button>
					</div>
				)}
			</div>
		</LoaderComponent>
	);
};

export default MultipleUploadTechpack;
